import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowRight,
	AudioLines,
	BadgeCheck,
	Check,
	Clock3,
	Film,
	Image,
	KeyRound,
	Library,
	Lock,
	type LucideIcon,
	RefreshCcw,
	ServerCog,
	Sparkles,
} from "lucide-react";
import { useState } from "react";
import { FaqSection } from "@/components/faq-section";
import { PricingTable } from "@/components/pricing-table";
import {
	MAIN_CONTENT_ID,
	PublicFooter,
	PublicHeader,
} from "@/components/public-layout";
import { Reveal } from "@/components/reveal";
import { buildPageHead } from "@/lib/seo-meta";

const CONNECT_SECTION_ID = "connect";
const PRICING_SECTION_ID = "pricing";
const WORKFLOW_SECTION_ID = "workflow";
const HERO_GRID_CELLS = Array.from({ length: 64 }, (_, index) => {
	const row = Math.floor(index / 8) + 1;
	const column = (index % 8) + 1;
	return {
		id: `hero-grid-cell-${row}-${column}`,
		row,
		column,
		isCoveredByHero: row >= 2 && row <= 7 && column >= 2 && column <= 7,
	};
}).filter((cell) => !cell.isCoveredByHero);

export const Route = createFileRoute("/")({
	component: HomePage,
	head: ({ matches }) => {
		const pageHead = buildPageHead({
			matches,
			internalPath: "/",
			title: "AgentFramer - Create Content With Your AI Agent",
			description:
				"Use Claude, ChatGPT, Cursor, or any MCP client to create images, videos, audio, and campaign assets from one chat.",
			ogImage: "default",
		});

		return {
			...pageHead,
			links: [
				{
					rel: "preload",
					as: "image",
					href: "/logo.png",
					fetchPriority: "low",
				},
				{
					rel: "preload",
					as: "image",
					href: "/showcase-bg.webp",
					fetchPriority: "low",
				},
				...pageHead.links,
			],
		};
	},
});

function HomePage() {
	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader framed />

			<main id={MAIN_CONTENT_ID} className="relative z-10 flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						<Hero />
						<AgentInstallSection id={CONNECT_SECTION_ID} />
						<WorkflowShowcase />
						<TrustStrip />
						<Capabilities />
						<ArchitectureSection />
						<section
							id={PRICING_SECTION_ID}
							className="homepage-hover-cell rounded-md bg-background py-10"
						>
							<Reveal className="px-8 pb-8">
								<p className="font-mono text-[11px] tracking-normal text-muted-foreground/70">
									Pricing
								</p>
								<h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
									Prepaid balance. No subscription.
								</h2>
								<p className="mt-2 max-w-2xl text-base text-muted-foreground">
									Top up before you generate. AgentFramer deducts usage only
									when image, video, or audio jobs run.
								</p>
							</Reveal>
							<Reveal delay={120}>
								<PricingTable />
							</Reveal>
						</section>
						<FaqSection />
					</div>
				</div>
			</main>

			<PublicFooter />
		</div>
	);
}

function Hero() {
	return (
		<section className="relative overflow-hidden rounded-md bg-white [--hero-row-size:4.75rem] dark:bg-black sm:bg-border dark:sm:bg-border sm:[--hero-row-size:5.25rem]">
			<div
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 sm:hidden"
				style={{
					backgroundImage:
						"linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)",
					backgroundSize: "25% var(--hero-row-size)",
				}}
			/>
			<div className="relative sm:grid sm:grid-cols-8 sm:grid-rows-[repeat(8,minmax(var(--hero-row-size),auto))] sm:gap-px">
				{HERO_GRID_CELLS.map((cell) => (
					<div
						key={cell.id}
						aria-hidden="true"
						className="pointer-events-none hidden rounded-md bg-white dark:bg-black sm:block"
						style={{
							gridColumn: cell.column,
							gridRow: cell.row,
						}}
					/>
				))}
				<div className="homepage-hover-cell homepage-hero-cell relative mx-auto flex w-full max-w-[calc(100%-2px)] flex-col items-center justify-center rounded-md bg-white px-5 py-9 text-center dark:bg-black sm:col-start-2 sm:col-end-8 sm:row-start-2 sm:row-end-8 sm:max-w-none sm:px-10 sm:py-12">
					<Reveal className="flex flex-col items-center">
						<h1 className="text-[3rem] font-semibold leading-[0.95] tracking-[-0.05em] sm:max-w-none sm:text-5xl sm:leading-[1.1] sm:tracking-[-0.04em] lg:text-6xl">
							<span className="block sm:inline">Create content with </span>
							<span className="inline-flex items-center whitespace-nowrap">
								<span className="text-brand-accent">one chat.</span>
								<Sparkles className="ml-2 size-8 text-brand-accent sm:size-10" />
							</span>
						</h1>
						<p className="mx-auto mt-3 max-w-[19rem] text-base leading-relaxed text-muted-foreground sm:max-w-lg sm:text-xl">
							Give your AI assistant one tool for images, video, audio, and
							campaign assets. Prompt once, get finished files back in chat.
						</p>
						<div className="mt-8 flex w-full max-w-[18rem] flex-col items-center justify-center gap-3 sm:max-w-none sm:flex-row">
							<Link
								to="/onboard"
								className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand-accent px-6 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-brand-accent/90 sm:w-auto"
							>
								Top up to create
								<ArrowRight className="size-3.5" />
							</Link>
							<a
								href={`#${WORKFLOW_SECTION_ID}`}
								className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-6 py-3 text-[15px] font-semibold text-foreground transition-colors hover:bg-muted/50 sm:w-auto"
							>
								See how it works
							</a>
						</div>
						<HeroToolStrip />
					</Reveal>
				</div>
			</div>
		</section>
	);
}

function HeroToolStrip() {
	const tools = [
		{ label: "generate_image", icon: Image },
		{ label: "generate_video", icon: Film },
		{ label: "generate_audio", icon: AudioLines },
		{ label: "list_models", icon: Library },
	];

	return (
		<div className="mt-8 hidden w-full flex-col items-center gap-4 sm:flex">
			<p className="text-[10px] font-medium uppercase tracking-normal text-muted-foreground">
				Tools your assistant can call
			</p>
			<div className="flex flex-wrap justify-center gap-2">
				{tools.map(({ label, icon: Icon }) => (
					<span
						key={label}
						className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/30 px-3 py-1.5 font-mono text-xs text-muted-foreground"
					>
						<Icon className="size-3.5 text-[#6B3FF2]" />
						{label}
					</span>
				))}
			</div>
		</div>
	);
}

function TrustStrip() {
	const items = [
		"Claude",
		"ChatGPT",
		"Cursor",
		"Windsurf",
		"Codex",
		"MCP clients",
	];

	return (
		<div className="homepage-hover-cell rounded-md bg-white px-5 py-6 text-center dark:bg-black sm:px-8">
			<Reveal>
				<p className="text-sm text-muted-foreground">
					For users and agents that need finished content assets, not another
					model dashboard.
				</p>
				<div className="mt-4 flex flex-wrap items-center justify-center gap-2">
					{items.map((item) => (
						<span
							key={item}
							className="rounded-full border border-border bg-muted/30 px-3 py-1 text-xs font-medium text-muted-foreground"
						>
							{item}
						</span>
					))}
				</div>
			</Reveal>
		</div>
	);
}

type ShowcaseView = "prompt" | "dispatch" | "poll" | "retrieve";

const SHOWCASE_NAV: Array<{
	id: ShowcaseView;
	label: string;
	icon: LucideIcon;
}> = [
	{ id: "prompt", label: "Prompt", icon: Sparkles },
	{ id: "dispatch", label: "Dispatch", icon: ServerCog },
	{ id: "poll", label: "Poll", icon: RefreshCcw },
	{ id: "retrieve", label: "Retrieve", icon: Library },
];

function WorkflowShowcase() {
	const [view, setView] = useState<ShowcaseView>("dispatch");

	return (
		<section
			id={WORKFLOW_SECTION_ID}
			className="homepage-hover-cell scroll-mt-20 rounded-md bg-background"
		>
			<Reveal>
				<div className="hidden px-4 py-3 sm:block sm:px-8 sm:py-4">
					<div
						className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-2"
						role="tablist"
						aria-label="Switch workflow preview"
					>
						{SHOWCASE_NAV.map(({ id, label, icon: Icon }) => {
							const isActive = id === view;
							return (
								<button
									key={id}
									type="button"
									role="tab"
									aria-selected={isActive}
									onClick={() => setView(id)}
									className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
										isActive
											? "border-[#6B3FF2] bg-background text-[#6B3FF2]"
											: "border-border bg-background text-foreground hover:bg-muted/60"
									}`}
								>
									<Icon className="size-3.5" />
									{label}
								</button>
							);
						})}
					</div>
				</div>
				<div
					className="border-t border-border px-3 pt-3 pb-0 sm:px-8 sm:pt-14"
					style={{
						backgroundImage:
							"radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(rgba(8,10,20,0.55), rgba(8,10,20,0.55)), url('/showcase-bg.webp')",
						backgroundSize: "14px 14px, 100% 100%, cover",
						backgroundPosition: "0 0, center, center",
						backgroundRepeat: "repeat, no-repeat, no-repeat",
					}}
				>
					<div className="homepage-hover-cell mx-auto max-w-4xl overflow-hidden rounded-t-xl border border-b-0 border-border bg-card">
						<div className="relative flex h-8 items-center border-b border-border bg-muted/40 px-3">
							<div className="flex gap-1.5">
								<span className="size-2.5 rounded-full bg-muted-foreground/30" />
								<span className="size-2.5 rounded-full bg-muted-foreground/30" />
								<span className="size-2.5 rounded-full bg-muted-foreground/30" />
							</div>
							<span className="absolute left-1/2 max-w-[70%] -translate-x-1/2 truncate text-center text-xs font-medium text-muted-foreground">
								AgentFramer - {SHOWCASE_NAV.find((n) => n.id === view)?.label}
							</span>
						</div>
						<div className="grid grid-cols-[56px_1fr] sm:grid-cols-[200px_1fr]">
							<aside className="flex flex-col gap-1 border-r border-border bg-muted/30 p-2 sm:p-3">
								<div className="mb-3 flex items-center gap-2 px-1.5 py-1 sm:px-2">
									<img
										src="/logo.svg"
										alt=""
										width="28"
										height="28"
										className="size-7 shrink-0 rounded-md"
									/>
									<span className="hidden truncate text-sm font-semibold sm:inline">
										AgentFramer
									</span>
								</div>
								{SHOWCASE_NAV.map(({ id, label, icon: Icon }) => {
									const isActive = id === view;
									return (
										<button
											key={id}
											type="button"
											onClick={() => setView(id)}
											aria-current={isActive ? "page" : undefined}
											className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
												isActive
													? "bg-[#6B3FF2]/10 font-medium text-[#6B3FF2]"
													: "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
											}`}
										>
											<Icon className="size-4 shrink-0" />
											<span className="hidden truncate sm:inline">{label}</span>
										</button>
									);
								})}
							</aside>
							<div className="min-w-0">
								<div className="h-[520px] overflow-hidden p-4 sm:h-[500px] sm:p-6">
									<ShowcaseBody view={view} />
								</div>
							</div>
						</div>
					</div>
				</div>
			</Reveal>
		</section>
	);
}

const ASSISTANT_CARDS = [
	{
		name: "Claude",
		description:
			"Best when Claude already helps plan launches, posts, and creative briefs.",
		features: [
			"Install with one command",
			"Turn prompts into image, video, and audio jobs",
			"Return finished asset URLs to the chat",
		],
		accent: "text-[#D97757]",
		border: "border-[#D97757]/25 hover:border-[#D97757]/45",
	},
	{
		name: "ChatGPT, Cursor, or any MCP client",
		description:
			"Best when your content workflow already lives inside an AI assistant.",
		features: [
			"Connect the hosted MCP server",
			"Create campaign assets without switching tabs",
			"Recover past generations from your workspace",
		],
		accent: "text-[#10A37F]",
		border: "border-[#10A37F]/25 hover:border-[#10A37F]/45",
	},
];

function AgentInstallSection({ id }: { id: string }) {
	return (
		<section
			id={id}
			className="homepage-hover-cell scroll-mt-20 overflow-hidden rounded-md bg-white dark:bg-black"
		>
			<Reveal>
				<div className="px-5 py-10 sm:px-8 sm:py-12">
					<h2 className="text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
						Create with Claude, ChatGPT, Cursor, or your own agent
					</h2>
					<p className="mt-2 max-w-xl text-base leading-7 text-muted-foreground">
						Set up AgentFramer once, then ask for the visuals, clips, sounds,
						and launch assets your workflow needs.
					</p>

					<div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
						{ASSISTANT_CARDS.map((assistant, index) => (
							<Reveal
								className="h-full"
								delay={120 + index * 80}
								key={assistant.name}
							>
								<div
									className={`group relative flex h-full flex-col gap-5 overflow-hidden rounded-lg border bg-background p-5 transition-colors duration-200 sm:p-6 ${assistant.border}`}
								>
									<div className="flex items-center gap-3">
										<span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
											<Sparkles className={`size-5 ${assistant.accent}`} />
										</span>
										<span className="text-lg font-semibold text-foreground">
											{assistant.name}
										</span>
									</div>
									<p className="text-sm leading-6 text-muted-foreground">
										{assistant.description}
									</p>
									<ul className="grid grid-cols-1 gap-2.5">
										{assistant.features.map((feature) => (
											<li key={feature} className="flex items-start gap-2">
												<Check
													className={`mt-0.5 size-4 shrink-0 ${assistant.accent}`}
													aria-hidden="true"
												/>
												<span className="text-sm leading-5 text-foreground/80">
													{feature}
												</span>
											</li>
										))}
									</ul>
									<Link
										to="/onboard"
										className="group/cta mt-auto inline-flex items-center justify-center gap-2 rounded-md bg-brand-accent px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-accent/90"
									>
										Create assets
										<ArrowRight className="size-4 transition-transform group-hover/cta:translate-x-0.5" />
									</Link>
								</div>
							</Reveal>
						))}
					</div>

					<Reveal delay={260}>
						<div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
							<Lock className="size-4 shrink-0" aria-hidden="true" />
							<span>
								Outputs are saved to your workspace and only generated when you
								ask.
							</span>
						</div>
					</Reveal>
				</div>
			</Reveal>
		</section>
	);
}

function ShowcaseBody({ view }: { view: ShowcaseView }) {
	switch (view) {
		case "prompt":
			return <PromptView />;
		case "dispatch":
			return <DispatchView />;
		case "poll":
			return <PollView />;
		case "retrieve":
			return <RetrieveView />;
	}
}

function PromptView() {
	return (
		<div className="space-y-4">
			<PanelTitle
				title="Agent receives the request"
				subtitle="A user asks for media. The agent calls an MCP tool."
			/>
			<ChatBubble from="user">
				Make a vertical product video with a soundtrack and a thumbnail image.
			</ChatBubble>
			<ChatBubble from="agent">
				Calling AgentFramer. I will dispatch each job, track status, and return
				the asset URLs.
			</ChatBubble>
			<CodeBlock
				code={`tool: generate_video
prompt: "vertical cinematic product loop..."
aspectRatio: "9:16"
durationSeconds: 8`}
			/>
		</div>
	);
}

function DispatchView() {
	return (
		<div className="space-y-4">
			<PanelTitle
				title="Dispatch to providers"
				subtitle="AgentFramer routes each job to the right model."
			/>
			<div className="grid gap-3 sm:grid-cols-3">
				{[
					{ label: "Image", status: "queued", icon: Image },
					{ label: "Video", status: "running", icon: Film },
					{ label: "Audio", status: "queued", icon: AudioLines },
				].map(({ label, status, icon: Icon }) => (
					<div
						key={label}
						className="rounded-lg border border-border bg-background p-4"
					>
						<Icon className="size-5 text-[#6B3FF2]" />
						<p className="mt-3 text-sm font-medium">{label}</p>
						<p className="mt-1 font-mono text-xs text-muted-foreground">
							{status}
						</p>
					</div>
				))}
			</div>
			<CodeBlock
				code={`{
  "generationId": "gen_8h2...",
  "tool": "generate_video",
  "status": "dispatched",
  "provider": "managed"
}`}
			/>
		</div>
	);
}

function PollView() {
	return (
		<div className="space-y-4">
			<PanelTitle
				title="Poll for completion"
				subtitle="Transparent status while jobs run async."
			/>
			{[
				["00:01", "jobs dispatched"],
				["00:07", "image complete"],
				["00:18", "audio complete"],
				["00:31", "video complete"],
			].map(([time, label]) => (
				<div
					key={time}
					className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3"
				>
					<span className="font-mono text-xs text-muted-foreground">
						{time}
					</span>
					<span className="text-sm">{label}</span>
					<BadgeCheck className="size-4 text-[#2b9a66]" />
				</div>
			))}
		</div>
	);
}

function RetrieveView() {
	return (
		<div className="space-y-4">
			<PanelTitle
				title="Retrieve the output"
				subtitle="Durable URLs returned to the agent and saved to the workspace."
			/>
			<div className="grid gap-3 sm:grid-cols-2">
				{[
					{ kind: "image/png", path: "/generations/image/gen_8h2.png" },
					{ kind: "video/mp4", path: "/generations/video/gen_8h2.mp4" },
					{ kind: "audio/mpeg", path: "/generations/audio/gen_8h2.mp3" },
					{ kind: "history", path: "dashboard/generations" },
				].map((asset) => (
					<div
						key={asset.path}
						className="rounded-lg border border-border bg-background p-4"
					>
						<p className="font-mono text-xs text-[#6B3FF2]">{asset.kind}</p>
						<p className="mt-2 truncate font-mono text-xs text-muted-foreground">
							{asset.path}
						</p>
					</div>
				))}
			</div>
			<ChatBubble from="agent">
				Done. Video, soundtrack, and thumbnail are ready. URLs returned and
				saved to your workspace.
			</ChatBubble>
		</div>
	);
}

function PanelTitle({ title, subtitle }: { title: string; subtitle: string }) {
	return (
		<div>
			<h3 className="text-lg font-semibold">{title}</h3>
			<p className="text-sm text-muted-foreground">{subtitle}</p>
		</div>
	);
}

function ChatBubble({
	children,
	from,
}: {
	children: string;
	from: "agent" | "user";
}) {
	return (
		<div
			className={`max-w-[85%] rounded-2xl border border-border px-4 py-3 text-sm leading-relaxed ${
				from === "agent"
					? "ml-auto bg-[#6B3FF2]/10 text-foreground"
					: "bg-background text-muted-foreground"
			}`}
		>
			{children}
		</div>
	);
}

function CodeBlock({ code }: { code: string }) {
	return (
		<pre className="overflow-x-auto rounded-lg border border-border bg-black p-4 text-left font-mono text-xs leading-relaxed text-white">
			<code>{code}</code>
		</pre>
	);
}

function Capabilities() {
	const capabilities = [
		{
			icon: Image,
			title: "Image generation",
			body: "Generate production-quality images from any prompt.",
		},
		{
			icon: Film,
			title: "Video generation",
			body: "Turn text or images into video clips, automatically.",
		},
		{
			icon: AudioLines,
			title: "Audio generation",
			body: "Create music, voice, and sound effects on demand.",
		},
		{
			icon: Library,
			title: "Generation history",
			body: "Every output stored, retrievable, and shareable.",
		},
		{
			icon: KeyRound,
			title: "MCP integration",
			body: "Works with Claude, Cursor, Windsurf, and any MCP-compatible client.",
		},
		{
			icon: Clock3,
			title: "Async-safe execution",
			body: "Agents poll, retrieve, and resume long jobs without custom queue glue.",
		},
	];

	return (
		<section className="@container flex flex-col gap-px bg-border">
			<div className="homepage-hover-cell rounded-md bg-background">
				<Reveal className="px-8 py-10 sm:py-12">
					<p className="font-mono text-[11px] tracking-normal text-muted-foreground/70">
						Capabilities
					</p>
					<h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
						What your agent can do through one MCP connection.
					</h2>
					<p className="mt-2 max-w-xl text-base text-muted-foreground">
						Prompt. Generate. Retrieve. All from your AI agent.
					</p>
				</Reveal>
			</div>
			<div className="grid grid-cols-1 gap-px @2xl:grid-cols-2 @4xl:grid-cols-3">
				{capabilities.map((capability, index) => {
					const Icon = capability.icon;
					return (
						<div
							key={capability.title}
							className="homepage-hover-cell rounded-md bg-background p-8"
						>
							<Reveal delay={(index % 3) * 80}>
								<Icon className="size-5 text-[#6B3FF2]" />
								<h3 className="mt-4 text-xl font-semibold">
									{capability.title}
								</h3>
								<p className="mt-2 text-sm leading-relaxed text-muted-foreground">
									{capability.body}
								</p>
							</Reveal>
						</div>
					);
				})}
			</div>
		</section>
	);
}

function ArchitectureSection() {
	return (
		<section className="homepage-hover-cell rounded-md bg-background px-8 py-12">
			<Reveal>
				<div className="grid gap-10 lg:grid-cols-[0.9fr_1.2fr] lg:items-center">
					<div>
						<p className="font-mono text-[11px] tracking-normal text-muted-foreground/70">
							MCP-native infrastructure
						</p>
						<h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
							The content creation layer for AI agents.
						</h2>
						<p className="mt-4 text-base leading-relaxed text-muted-foreground">
							Direct model APIs leave your agent juggling providers, queues,
							storage, and file URLs. AgentFramer turns image, video, and audio
							creation into one MCP interface with job status, retrieval, and
							workspace history built in.
						</p>
					</div>
					<div />
				</div>
			</Reveal>
		</section>
	);
}
